import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useGetList } from "ra-core";
import { CircleMarker, MapContainer, Popup, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import type { Company } from "@/components/atomic-crm/types";

type GeoCompany = Company & {
  latitude?: number | null;
  longitude?: number | null;
};

/** Centre approximatif de la Vendée, cadré sur le littoral. */
const CENTRE: [number, number] = [46.62, -1.55];
const TOUTES = "__toutes__";

/** Bleu = à joindre sur place, vert = on a déjà un numéro. */
const COULEURS = {
  avecTel: "#16a34a",
  sansTel: "#2563eb",
};

export const MapPage = () => {
  const { companySectors } = useConfigurationContext();
  const [ville, setVille] = useState(TOUTES);
  const [secteur, setSecteur] = useState(TOUTES);
  const [sansTelSeulement, setSansTelSeulement] = useState(false);

  // La carte affiche l'ensemble du portefeuille : on charge large plutôt que
  // de paginer, quitte à filtrer côté client — c'est instantané et ça évite
  // un aller-retour réseau à chaque changement de filtre.
  // Pas de filtre serveur sur la latitude : l'opérateur `not.is` n'est pas
  // couvert par l'adaptateur FakeRest, et le tri des géolocalisées se fait
  // très bien côté client.
  const { data, isPending } = useGetList<GeoCompany>("companies", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "name", order: "ASC" },
  });

  const geolocalisees = useMemo(
    () => (data ?? []).filter((c) => c.latitude != null && c.longitude != null),
    [data],
  );

  const villes = useMemo(
    () =>
      Array.from(
        new Set(geolocalisees.map((c) => c.city).filter(Boolean)),
      ).sort((a, b) => (a as string).localeCompare(b as string)) as string[],
    [geolocalisees],
  );

  const visibles = useMemo(
    () =>
      geolocalisees.filter(
        (c) =>
          (ville === TOUTES || c.city === ville) &&
          (secteur === TOUTES || c.sector === secteur) &&
          (!sansTelSeulement || !c.phone_number),
      ),
    [geolocalisees, ville, secteur, sansTelSeulement],
  );

  const avecTel = visibles.filter((c) => c.phone_number).length;

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={ville} onValueChange={setVille}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Toutes les communes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TOUTES}>
              Toutes les communes ({villes.length})
            </SelectItem>
            {villes.map((v) => (
              <SelectItem key={v} value={v}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={secteur} onValueChange={setSecteur}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Tous les types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TOUTES}>Tous les types</SelectItem>
            {companySectors.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant={sansTelSeulement ? "default" : "outline"}
          onClick={() => setSansTelSeulement((v) => !v)}
        >
          À visiter sur place
        </Button>

        <div className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="secondary">{visibles.length} sur la carte</Badge>
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: COULEURS.avecTel }}
            />
            {avecTel} avec téléphone
          </span>
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: COULEURS.sansTel }}
            />
            {visibles.length - avecTel} à visiter
          </span>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <CardContent className="p-0">
          <MapContainer
            center={CENTRE}
            zoom={9}
            scrollWheelZoom
            style={{ height: "70vh", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {visibles.map((c) => (
              <CircleMarker
                key={c.id}
                center={[c.latitude as number, c.longitude as number]}
                radius={6}
                pathOptions={{
                  color: c.phone_number ? COULEURS.avecTel : COULEURS.sansTel,
                  fillOpacity: 0.7,
                  weight: 1.5,
                }}
              >
                <Popup>
                  <div className="text-sm space-y-1">
                    <Link
                      to={`/companies/${c.id}/show`}
                      className="font-semibold underline"
                    >
                      {c.name}
                    </Link>
                    <div className="text-muted-foreground">{c.address}</div>
                    {c.phone_number ? (
                      <a
                        href={`tel:${c.phone_number}`}
                        className="block underline"
                      >
                        {c.phone_number}
                      </a>
                    ) : (
                      <div className="italic text-muted-foreground">
                        Pas de téléphone — à visiter
                      </div>
                    )}
                    <a
                      className="block underline"
                      target="_blank"
                      rel="noreferrer"
                      href={`https://www.google.com/maps/dir/?api=1&destination=${c.latitude},${c.longitude}`}
                    >
                      Itinéraire
                    </a>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </CardContent>
      </Card>
    </div>
  );
};

MapPage.path = "/carte";
